import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { http, formatIDR, whatsappHref, formatPhoneDisplay, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import {
  HourglassMedium,
  WhatsappLogo,
  ArrowUpRight,
  Warning,
  ListPlus,
  User,
  Funnel,
  Buildings,
  CheckCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, inputCls, ISO_SERVICES, ACCREDITATIONS } from "@/pages/PipelinePage";

const THRESHOLDS = [
  { label: "Semua", value: 0 },
  { label: "7d+", value: 7 },
  { label: "14d+", value: 14 },
  { label: "30d+", value: 30 },
  { label: "60d+", value: 60 },
];

export const ALLOWED_SOURCES = [
  "Website",
  "Telemarketing",
  "Referral",
  "Repeat Order Website",
  "Repeat Order Referral",
  "Repeat Order Konsultan",
];

export default function LeadsIdlePage() {
  const { t } = useI18n();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [accreditationFilter, setAccreditationFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Task Creation Modal for Contact
  const [taskContact, setTaskContact] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [idleRes, usersRes] = await Promise.all([
        http.get(`/analytics/leads-idle?days=${days}`),
        http.get("/users"),
      ]);
      setData(idleRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error("Load leads idle error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  // Client-side filtering
  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((c) => {
      const matchSearch =
        !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        (c.company && c.company.toLowerCase().includes(search.toLowerCase())) ||
        (c.phone && c.phone.includes(search));

      const matchOwner = ownerFilter === "all" || c.owner_id === ownerFilter;
      const matchSource = sourceFilter === "all" || c.source === sourceFilter;

      let matchService = true;
      if (serviceFilter !== "all") {
        const inContactServices = Array.isArray(c.services) && c.services.includes(serviceFilter);
        const inDeals = Array.isArray(c.deals) && c.deals.some((d) => d.service === serviceFilter);
        matchService = inContactServices || inDeals;
      }

      let matchAccreditation = true;
      if (accreditationFilter !== "all") {
        matchAccreditation =
          Array.isArray(c.deals) && c.deals.some((d) => d.accreditation === accreditationFilter);
      }

      return matchSearch && matchOwner && matchSource && matchService && matchAccreditation;
    });
  }, [data, search, ownerFilter, sourceFilter, serviceFilter, accreditationFilter]);

  const severity = (n) =>
    n >= 60 ? "#D63230" : n >= 30 ? "#F9A523" : n >= 14 ? "#C28E0E" : "#5A6B76";

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">
            Follow-up Radar
          </div>
          <h1
            className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]"
            data-testid="leads-idle-title"
          >
            Leads Idle
          </h1>
          <p className="text-[#64748B] mt-1 text-xs sm:text-sm">
            Kontak yang telah diakuisisi namun belum pernah melangkah ke tahap Proposal. Pantau dan tindak lanjuti sebelum mendingin.
          </p>
        </div>

        {/* Threshold Switcher */}
        <div className="flex flex-wrap items-center gap-1.5 pt-2 sm:pt-0 border-t sm:border-t-0 border-[#E2E8F0]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold mr-1">
            Threshold:
          </div>
          {THRESHOLDS.map((th) => (
            <button
              key={th.value}
              onClick={() => setDays(th.value)}
              data-testid={`threshold-${th.value}`}
              className={`rounded-lg border px-3 py-1.5 text-xs font-mono-data font-semibold transition-colors ${
                days === th.value
                  ? "bg-[#173B4D] border-[#173B4D] text-white shadow-2xs"
                  : "border-[#CBD5E1] bg-white text-[#64748B] hover:border-[#173B4D]"
              }`}
            >
              {th.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-3 sm:p-4 rounded-xl border border-[#E2E8F0] shadow-2xs mb-5 flex flex-wrap items-center gap-2.5">
        <div className="flex-1 min-w-[160px]">
          <input
            type="text"
            placeholder="Cari nama, perusahaan, telepon..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs text-[#173042] placeholder:text-slate-400 focus:outline-none focus:border-[#173B4D]"
          />
        </div>

        {/* Owner Filter */}
        {users.length > 0 && (
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            data-testid="leads-filter-owner"
            className="px-2.5 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#475569] focus:outline-none focus:border-[#173B4D]"
          >
            <option value="all">Semua Sales</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        )}

        {/* Lead Source Filter */}
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          data-testid="leads-filter-source"
          className="px-2.5 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#475569] focus:outline-none focus:border-[#173B4D]"
        >
          <option value="all">Semua Sumber</option>
          {ALLOWED_SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {/* ISO Service Filter */}
        <select
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
          data-testid="leads-filter-service"
          className="px-2.5 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#475569] focus:outline-none focus:border-[#173B4D]"
        >
          <option value="all">Semua Layanan ISO</option>
          {ISO_SERVICES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {/* Accreditation Filter */}
        <select
          value={accreditationFilter}
          onChange={(e) => setAccreditationFilter(e.target.value)}
          data-testid="leads-filter-accreditation"
          className="px-2.5 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#475569] focus:outline-none focus:border-[#173B4D]"
        >
          <option value="all">Semua Akreditasi</option>
          {ACCREDITATIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-xs text-[#64748B] uppercase tracking-[0.15em] font-semibold py-8">
          Memuat data leads idle...
        </div>
      ) : (
        <>
          {/* Summary Stat Cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-5 sm:mb-6">
            <StatCard
              label="Leads Belum Proposal"
              value={filteredItems.length}
              icon={HourglassMedium}
              accent="#D9A321"
              testid="idle-count"
            />
            <StatCard
              label="Ambang Batas"
              value={days === 0 ? "Semua" : `${days} hari`}
              icon={Warning}
              accent="#173B4D"
            />
            <StatCard
              label="Idle Terlama"
              value={filteredItems[0] ? `${filteredItems[0].days_idle}d` : "-"}
              icon={HourglassMedium}
              accent="#D63230"
            />
          </div>

          {/* Mobile Card List (< md) */}
          <div className="md:hidden space-y-3 mb-6" data-testid="leads-idle-mobile-list">
            {filteredItems.length === 0 && (
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-sm text-[#64748B]">
                <div className="font-bold text-base text-[#173042] mb-1">
                  Bagus! Tidak ada leads terdiam.
                </div>
                <div>Semua kontak Anda telah mencapai Proposal atau dalam pantauan aktif.</div>
              </div>
            )}
            {filteredItems.map((c) => (
              <div
                key={c.id}
                className="bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-2xs space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-bold text-sm text-[#173042]">{c.name}</div>
                    {c.company && (
                      <div className="text-xs text-[#64748B] mt-0.5">{c.company}</div>
                    )}
                  </div>
                  <span
                    className="font-mono-data text-xs font-bold px-2 py-0.5 rounded-full bg-red-50"
                    style={{ color: severity(c.days_idle) }}
                  >
                    {c.days_idle} hari idle
                  </span>
                </div>

                {/* Services & Source */}
                <div className="flex flex-wrap items-center gap-1">
                  {(c.services || []).slice(0, 3).map((s) => (
                    <span
                      key={s}
                      className="text-[9px] uppercase font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded"
                    >
                      {s}
                    </span>
                  ))}
                  {c.source && (
                    <span className="text-[9px] font-medium bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                      {c.source}
                    </span>
                  )}
                  {c.deals && c.deals.length > 0 ? (
                    <span className="text-[9px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                      Tahap: {t(`stage.${c.deals[0].stage}`) || c.deals[0].stage}
                    </span>
                  ) : (
                    <span className="text-[9px] text-slate-400 italic">Belum ada deal</span>
                  )}
                </div>

                {/* Action Row */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F1F5F9]">
                  <div className="text-[11px] font-mono-data text-[#64748B]">
                    Updated:{" "}
                    {new Date(c.last_activity_at).toLocaleDateString("id-ID", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setTaskContact(c)}
                      className="inline-flex items-center gap-1 border border-[#CBD5E1] bg-white text-[#173042] px-2.5 py-1 rounded-lg text-xs font-semibold hover:bg-slate-50 transition-colors"
                      title="Buat Tugas Follow-up"
                    >
                      <ListPlus size={14} />
                      <span>Tugas</span>
                    </button>
                    {c.phone && (
                      <a
                        href={
                          whatsappHref(c.phone) +
                          `?text=${encodeURIComponent(
                            `Halo ${c.name}, mohon maaf mengganggu waktunya. Saya dari Nusacert ingin follow-up mengenai kebutuhan sertifikasi ISO perusahaan Bapak/Ibu. Boleh diskusi sebentar?`
                          )}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`follow-up-${c.id}`}
                        className="inline-flex items-center gap-1 bg-[#2E8B57] hover:bg-[#246B43] text-white px-2.5 py-1 rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                      >
                        <WhatsappLogo size={14} weight="fill" />
                        <span>WA</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table (>= md) */}
          <div className="hidden md:block bg-white rounded-xl border border-[#E2E8F0] overflow-x-auto shadow-2xs">
            <table className="w-full min-w-[900px]" data-testid="leads-idle-table">
              <thead>
                <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <Th>Nama & Perusahaan</Th>
                  <Th>WhatsApp</Th>
                  <Th>Sumber Lead</Th>
                  <Th>Layanan ISO</Th>
                  <Th>Status Deal</Th>
                  <Th className="text-right">Terakhir Diperbarui</Th>
                  <Th className="text-right">Idle</Th>
                  <Th className="text-right">Aksi</Th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-16 text-sm text-[#64748B]">
                      <div className="font-display text-2xl mb-2 text-[#173042]">
                        Bagus! Tidak ada leads terdiam.
                      </div>
                      <div>Semua prospek Anda telah mencapai Proposal atau aktif.</div>
                    </td>
                  </tr>
                )}
                {filteredItems.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                  >
                    <Td>
                      <Link
                        to="/contacts"
                        className="font-semibold text-xs sm:text-sm text-[#173042] hover:underline"
                      >
                        {c.name}
                      </Link>
                      <div className="text-[11px] text-[#64748B]">{c.company || "-"}</div>
                    </Td>
                    <Td>
                      {c.phone ? (
                        <a
                          href={whatsappHref(c.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[#2E8B57] hover:text-[#25D366] font-mono-data text-xs font-semibold"
                        >
                          <WhatsappLogo size={15} weight="fill" />{" "}
                          {formatPhoneDisplay(c.phone)}
                        </a>
                      ) : (
                        "-"
                      )}
                    </Td>
                    <Td>
                      <span className="text-xs text-[#64748B]">{c.source || "-"}</span>
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(c.services || []).length === 0 && (
                          <span className="text-xs text-[#64748B]">-</span>
                        )}
                        {(c.services || []).slice(0, 3).map((s) => (
                          <span
                            key={s}
                            className="text-[9px] uppercase font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </Td>
                    <Td>
                      {c.deals && c.deals.length > 0 ? (
                        <span className="text-[10px] font-bold bg-amber-50 text-amber-800 px-2 py-0.5 rounded border border-amber-200">
                          {t(`stage.${c.deals[0].stage}`) || c.deals[0].stage}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Belum ada deal</span>
                      )}
                    </Td>
                    <Td className="text-right font-mono-data text-xs text-[#64748B]">
                      {formatDate(c.last_activity_at)}
                    </Td>
                    <Td className="text-right">
                      <span
                        className="font-mono-data text-xs font-bold"
                        style={{ color: severity(c.days_idle) }}
                      >
                        {c.days_idle} hari
                      </span>
                    </Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setTaskContact(c)}
                          className="inline-flex items-center gap-1 border border-[#CBD5E1] bg-white hover:bg-slate-50 text-[#173042] px-2.5 py-1.5 rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                          title="Buat Tugas Follow-Up"
                        >
                          <ListPlus size={14} />
                          <span>Tugas</span>
                        </button>
                        {c.phone && (
                          <a
                            href={
                              whatsappHref(c.phone) +
                              `?text=${encodeURIComponent(
                                `Halo ${c.name}, mohon maaf mengganggu waktunya. Saya dari Nusacert ingin follow-up mengenai kebutuhan sertifikasi ISO perusahaan Bapak/Ibu. Boleh diskusi sebentar?`
                              )}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`follow-up-${c.id}`}
                            className="inline-flex items-center gap-1.5 bg-[#2E8B57] hover:bg-[#246B43] text-white px-3 py-1.5 rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                          >
                            <WhatsappLogo size={14} weight="fill" />
                            <span>Follow-up</span>
                          </a>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Follow-up Task Creation Modal */}
      {taskContact && (
        <CreateFollowUpTaskModal
          contact={taskContact}
          onClose={() => setTaskContact(null)}
          onSaved={() => {
            setTaskContact(null);
            load();
          }}
        />
      )}
    </AppLayout>
  );
}

function CreateFollowUpTaskModal({ contact, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    title: `Follow up ${contact.name}`,
    description: `Tindak lanjut kebutuhan sertifikasi ISO untuk ${contact.company || contact.name}`,
    priority: "medium",
    due_date: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await http.post("/tasks", {
        title: form.title,
        description: form.description,
        priority: form.priority,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : undefined,
        contact_id: contact.id,
      });
      toast.success("Tugas follow-up berhasil dibuat");
      onSaved();
    } catch (err) {
      const serverMsg =
        err?.response?.data?.detail && typeof err.response.data.detail === "string"
          ? err.response.data.detail
          : null;
      toast.error(serverMsg || "Gagal membuat tugas follow-up");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl border border-[#E2E8F0] w-full max-w-md overflow-hidden page-enter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-3.5 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC]">
          <div>
            <h3 className="font-bold text-sm sm:text-base text-[#173042]">
              Tugas Follow-Up Leads
            </h3>
            <p className="text-[11px] text-[#64748B]">Untuk: {contact.name}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-[#64748B] hover:text-[#173042]">
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="p-4 sm:p-6 space-y-3.5">
          <Field label="Judul Tugas" required>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Prioritas">
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className={inputCls}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Field>

            <Field label="Jatuh Tempo">
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Deskripsi">
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={inputCls}
            />
          </Field>

          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-xs font-semibold text-[#475569]"
            >
              Batal
            </button>
            <button
              disabled={saving}
              type="submit"
              className="bg-[#173B4D] text-white px-5 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
            >
              {saving ? "Menyimpan..." : "Simpan Tugas"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      className="bg-white rounded-xl border border-[#E2E8F0] border-t-4 p-2.5 sm:p-4 shadow-2xs flex flex-col justify-between"
      style={{ borderTopColor: accent }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.1em] text-[#64748B] font-bold truncate">
          {label}
        </div>
        <Icon size={14} className="text-[#64748B] shrink-0" />
      </div>
      <div className="font-mono-data text-base sm:text-2xl font-bold text-[#173042] mt-1.5 truncate">
        {value}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th
    className={`text-left py-3 px-4 font-bold uppercase tracking-wider text-[10px] text-[#64748B] ${className}`}
  >
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`py-3.5 px-4 text-xs text-[#173042] ${className}`}>{children}</td>
);
